/**
 * CommunityRitualCard Component
 * Full-bleed image card with community overlay, contextual tag, and bottom gradient info area.
 * Matches Figma: asymmetric rounding, 640x440 aspect, backdrop-blur gradient footer.
 */

import { Link } from "react-router-dom"
import { formatLocation } from "@/components/shared/formatLocation"
import type { CommunityRitual } from "../types/connect.types"

interface CommunityRitualCardProps {
  ritual: CommunityRitual
  onJoinCommunity?: (communityId: string) => void
}

export function CommunityRitualCard({ ritual, onJoinCommunity }: CommunityRitualCardProps) {
  const community = ritual.community
  const communityLocation = formatLocation(community.city, community.state)

  const memberStatus = community.memberStatus

  const priceBadgeConfig = () => {
    if (ritual.participationFee === "Donation-based") {
      return { label: "Donation-based", bg: "bg-brand-terracotta", text: "text-white" }
    }
    if (ritual.participationFee === "Paid") {
      return { label: "Paid", bg: "bg-purple text-white", text: "text-white" }
    }
    return { label: "Free", bg: "bg-brand-olive text-white", text: "text-white" }
  }

  const getButtonConfig = () => {
    if (memberStatus === "JOINED" || memberStatus === "PENDING") {
      return null
    }
    return {
      label: "Follow",
      onClick: () => onJoinCommunity?.(community.id),
      className:
        "bg-connect-cream text-connect-cream-text hover:opacity-90 transition-opacity cursor-pointer",
    }
  }

  const buttonConfig = getButtonConfig()

  return (
    <div className="relative rounded-tl-[40px] rounded-br-[40px] bg-white overflow-hidden aspect-[640/440]">
      {/* Full-bleed cover image */}
      <Link to={`/rituals/${ritual.id}`} className="absolute inset-0 block">
        {ritual.coverImage ? (
          <img
            src={ritual.coverImage}
            alt={ritual.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-beige-medium" />
        )}
      </Link>

      {/* Top-left: Community avatar + name + location */}
      <Link
        to={`/communities/${community.id}`}
        className="absolute left-[20px] top-[20px] flex items-center gap-2 z-10 hover:opacity-80 transition-opacity"
      >
        <div className="w-[50px] h-[50px] rounded-[10px] overflow-hidden flex-shrink-0 bg-beige-medium">
          {community.coverImage ? (
            <img
              src={community.coverImage}
              alt={community.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm font-medium text-text-primary">
              {community.name?.[0] || "?"}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-white truncate drop-shadow-sm">
            {community.name}
          </p>
          {communityLocation && (
            <p className="text-[12px] text-white/80 truncate drop-shadow-sm">{communityLocation}</p>
          )}
        </div>
      </Link>

      {/* Top-right: Follow button — hidden when JOINED or PENDING */}
      {buttonConfig && (
        <button
          type="button"
          onClick={buttonConfig.onClick}
          className={`absolute right-[20px] top-[20px] z-10 h-[50px] w-[120px] rounded-full flex items-center justify-center text-[12px] font-semibold uppercase ${buttonConfig.className}`}
        >
          {buttonConfig.label}
        </button>
      )}

      {/* Contextual tag */}
      {ritual.contextualTag && (
        <span className="absolute top-[300px] left-[20px] z-10 inline-block bg-brand-slate text-white text-[13px] rounded-[5px] px-[6px] py-[2px]">
          {ritual.contextualTag}
        </span>
      )}

      {/* Bottom gradient info area */}
      <div className="absolute bottom-0 left-0 right-0 h-[116px] z-10 bg-gradient-to-t from-[rgba(0, 0, 0, 0.4)] to-transparent backdrop-blur-[1px] flex flex-col justify-end px-[20px] pb-[16px]">
        <Link
          to={`/rituals/${ritual.id}`}
          className="text-[18px] font-semibold text-white opacity-90 line-clamp-1 hover:underline"
        >
          {ritual.title}
        </Link>
        <div className="flex items-center gap-[10px] mt-[6px]">
          <span
            className={`inline-block text-[13px] rounded-[5px] px-[6px] py-[2px] ${priceBadgeConfig().bg} ${priceBadgeConfig().text}`}
          >
            {priceBadgeConfig().label}
          </span>
          <span className="text-[13px] text-white/80">{ritual.ritualType}</span>
          {ritual.duration && <span className="text-[13px] text-white/80">{ritual.duration}</span>}
        </div>
      </div>
    </div>
  )
}
